import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchOwnMedia, uploadOwnMedia } from '@/data/sources-media';
import type { SavedAsset } from '@/types/media';
import {
  ACCEPTED_UPLOAD_TYPES,
  assetDisplayName,
  describeUploadError,
  formatBytes,
  validateUploadFile,
} from './ownMediaModel';

/* AI Content Studio — own-media ("My uploads") picker. Drag/drop + file-button
 * upload of the workspace's OWN images/logos/videos to POST /v1/media/upload
 * with real progress + honest backend validation errors, plus a grid of the
 * workspace's own uploads to preview, place (select), replace and reuse. It only
 * ever lists the caller's own workspace assets (server-scoped; no workspace
 * param is ever sent). Selection is surfaced via `onSelect` so the host wires it
 * into the studio's existing placement contract unchanged — this component never
 * touches the studio page. */

interface UploadState {
  name: string;
  percent: number | null; // null → indeterminate (honest)
  bytes: number;
}

export interface OwnMediaPickerProps {
  /** Fired when the user chooses an own asset to place/replace/reuse. The second
   *  arg is a local object-URL preview for a just-uploaded file, else null. */
  onSelect?: (asset: SavedAsset, previewUrl: string | null) => void;
}

export function OwnMediaPicker({ onSelect }: OwnMediaPickerProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<UploadState | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Local object-URLs for files uploaded this session → real, honest thumbnails
  // even before the server exposes a resolvable preview URL.
  const [localPreviews, setLocalPreviews] = useState<Record<number, string>>({});

  const list = useQuery({
    queryKey: ['own-media'],
    queryFn: () => fetchOwnMedia(),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const objectUrl = URL.createObjectURL(file);
      try {
        const res = await uploadOwnMedia(file, {
          onProgress: (p) => setUploading({ name: file.name, percent: p.percent, bytes: file.size }),
        });
        return { res, objectUrl };
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        throw err;
      }
    },
    onSuccess: ({ res, objectUrl }) => {
      const id = res.asset.id;
      if (id != null) {
        setLocalPreviews((prev) => ({ ...prev, [id]: objectUrl }));
        setSelectedId(id);
      } else {
        URL.revokeObjectURL(objectUrl);
      }
      void queryClient.invalidateQueries({ queryKey: ['own-media'] });
    },
    onSettled: () => setUploading(null),
  });

  const handleFiles = useCallback(
    (files: FileList | File[] | null) => {
      setClientError(null);
      const arr = files ? Array.from(files) : [];
      if (arr.length === 0) return;
      // Sequentially upload each file (progress shown for the active one).
      const next = (i: number) => {
        if (i >= arr.length) return;
        const file = arr[i];
        const check = validateUploadFile(file);
        if (!check.ok) {
          setClientError(check.reason);
          next(i + 1);
          return;
        }
        uploadMutation.mutate(file, { onSettled: () => next(i + 1) });
      };
      next(0);
    },
    [uploadMutation],
  );

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      {/* Upload dropzone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload your own media"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={
          'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-6 text-center transition-colors ' +
          (dragOver ? 'border-ai bg-ai/5' : 'border-border-default hover:border-ai/60')
        }
      >
        <span className="text-sm font-medium text-text-primary">Drag & drop, or click to choose a file</span>
        <span className="text-xs text-text-secondary">
          Images (PNG, JPEG, WebP, GIF) or video (MP4, WebM, MOV). Stored privately in this workspace.
        </span>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED_UPLOAD_TYPES.join(',')}
          multiple
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = ''; // allow re-selecting the same file
          }}
        />
      </div>

      {/* Active upload progress (determinate, or honestly indeterminate) */}
      {uploading && (
        <div className="space-y-1" aria-live="polite">
          <div className="flex items-center justify-between text-xs text-text-secondary">
            <span className="truncate pr-2">Uploading {uploading.name}…</span>
            <span>{uploading.percent === null ? formatBytes(uploading.bytes) : `${uploading.percent}%`}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded bg-inset">
            <div
              className={'h-full rounded bg-ai transition-[width] ' + (uploading.percent === null ? 'animate-pulse' : '')}
              style={{ width: uploading.percent === null ? '100%' : `${uploading.percent}%` }}
            />
          </div>
        </div>
      )}

      {clientError && (
        <p className="text-sm text-danger" role="alert">
          {clientError}
        </p>
      )}
      {uploadMutation.isError && (
        <p className="text-sm text-danger" role="alert">
          {describeUploadError(uploadMutation.error)}
        </p>
      )}

      <p className="text-xs text-text-secondary">Your uploads · private to this workspace</p>

      {/* Own-media grid */}
      {list.isLoading ? (
        <LoadingState rows={3} />
      ) : list.isError ? (
        <ErrorState error="Could not load your uploads." onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No uploads yet"
          teach="Upload an image, logo or video above. It’s stored privately in this workspace and you can reuse it across designs."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((asset) => (
            <OwnAssetCard
              key={asset.id ?? asset.storageRef}
              asset={asset}
              previewUrl={(asset.id != null ? localPreviews[asset.id] : undefined) ?? asset.previewUrl}
              selected={selectedId != null && asset.id === selectedId}
              canSelect={!!onSelect}
              onUse={() => {
                setSelectedId(asset.id);
                onSelect?.(asset, (asset.id != null ? localPreviews[asset.id] : undefined) ?? asset.previewUrl);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OwnAssetCard({
  asset,
  previewUrl,
  selected,
  canSelect,
  onUse,
}: {
  asset: SavedAsset;
  previewUrl: string | null | undefined;
  selected: boolean;
  canSelect: boolean;
  onUse: () => void;
}) {
  const isVideo = asset.kind === 'video';
  return (
    <Card className={'flex flex-col overflow-hidden ' + (selected ? 'ring-2 ring-ai' : '')}>
      <div className="relative aspect-[4/3] bg-inset">
        {previewUrl ? (
          isVideo ? (
            <video src={previewUrl} className="h-full w-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={previewUrl} alt={assetDisplayName(asset)} loading="lazy" className="h-full w-full object-cover" />
          )
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-2 text-center text-xs text-text-secondary">
            <span className="font-medium">{isVideo ? 'Video' : 'Image'}</span>
            <span className="truncate">{assetDisplayName(asset)}</span>
          </div>
        )}
        {isVideo && (
          <span className="pointer-events-none absolute left-2 top-2">
            <Badge tone="ai">Video</Badge>
          </span>
        )}
        {selected && (
          <span className="pointer-events-none absolute right-2 top-2">
            <Badge tone="success">Selected</Badge>
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 p-3">
        <span className="min-w-0 flex-1 truncate text-xs text-text-secondary" title={assetDisplayName(asset)}>
          {assetDisplayName(asset)}
          {asset.bytes ? ` · ${formatBytes(asset.bytes)}` : ''}
        </span>
        {canSelect && (
          <Button variant="secondary" onClick={onUse}>
            {selected ? 'Use again' : 'Use'}
          </Button>
        )}
      </div>
    </Card>
  );
}
